import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import Chip from "@mui/joy/Chip";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { ColorPaletteProp } from "@mui/joy/styles";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  ModalDialog,
  Typography,
} from "@mui/joy";
import Divider from "@mui/joy/Divider";
import { toast } from "react-toastify";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import { db } from "../../FirebaseConfig";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import Tooltip from "@mui/joy/Tooltip";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Switch from "@mui/joy/Switch";
import EditIcon from "@mui/icons-material/Edit";
import { Link } from "react-router-dom";

interface Props {
  data: any;
  rowsPerPage: number;
}

type DifficultyType = "Hard" | "Easy" | "Moderate";

const PaginationTable: React.FC<Props> = ({ data, rowsPerPage }) => {
  const [currentPage, setCurrentPage] = React.useState(0);
  const [selectedChallengeId, setSelectedChallengeId] = React.useState<
    string | null
  >(null);
  const [open, setOpen] = React.useState(false);
  const [currentRows, setCurrentRows] = React.useState(
    data.slice(0, rowsPerPage)
  );

  const DifficultyIcons: { [key in DifficultyType]: JSX.Element } = {
    Easy: <SentimentSatisfiedAltIcon />,
    Moderate: <SentimentSatisfiedIcon />,
    Hard: <SentimentVeryDissatisfiedIcon />,
  };

  const DifficultyColors: { [key in DifficultyType]: ColorPaletteProp } = {
    Easy: "success",
    Hard: "danger",
    Moderate: "warning",
  };

  const totalPages = Math.ceil(data.length / rowsPerPage);

  React.useEffect(() => {
    const indexOfLastRow = (currentPage + 1) * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    setCurrentRows(data.slice(indexOfFirstRow, indexOfLastRow));
  }, [data, currentPage, rowsPerPage]);

  const requestDelete = (challengeId: string) => {
    setSelectedChallengeId(challengeId);
    setOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const requestLive = (challengeId: string) => {
    const updatedRows = currentRows.map((row: any) => {
      if (row.id === challengeId) {
        return { ...row, live: !row.live };
      }
      return row;
    });
    setCurrentRows(updatedRows);
    handleLive(challengeId);
  };

  const handleDelete = async () => {
    if (selectedChallengeId) {
      try {
        await db.collection("ctf").doc(selectedChallengeId).delete();
        toast.success("Challenge deleted successfully");
        setOpen(false);
      } catch (error) {
        console.error("Error removing document: ", error);
        toast.error("Error deleting challenge");
      }
    }
  };

  const handleLive = async (challengeId: string) => {
    try {
      const challengeRef = db.collection("ctf").doc(challengeId);
      const docSnapshot = await challengeRef.get();

      if (!docSnapshot.exists) {
        console.error("Challenge not found with ID:", challengeId);
        toast.error("Challenge not found");
        return;
      }

      const currentLiveStatus = docSnapshot.data()?.live;
      const newLiveStatus = !currentLiveStatus;

      await challengeRef.update({ live: newLiveStatus });
      toast.success("Live status updated successfully");
    } catch (error) {
      console.error("Error updating live status: ", error);
      toast.error("Error updating live status");
    }
  };

  return (
    <>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: "80px",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Sr. No
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Category
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Challenge Name
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>Flag</th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Points
              </th>
              <th style={{ textAlign: "center", padding: "12px 6px" }}>
                Difficulty
              </th>
              <th
                style={{
                  width: "5%",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Live
              </th>
              <th
                style={{
                  width: "8%",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Edit
              </th>
              <th
                style={{
                  width: "8%",
                  textAlign: "center",
                  padding: "12px 6px",
                }}
              >
                Delete
              </th>
            </tr>
          </thead>

          <tbody>
            {currentRows &&
              currentRows.map((row: any, index: any) => (
                <tr key={row.id}>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">{index + 1}</Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {row.category ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {row.name ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {row.flag ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Typography level="body-xs">
                      {row.points ?? "None"}
                    </Typography>
                  </td>
                  <td style={{ textAlign: "center", padding: "12px 6px" }}>
                    <Chip
                      variant="soft"
                      size="sm"
                      startDecorator={
                        DifficultyIcons[row.difficulty as DifficultyType]
                      }
                      color={
                        DifficultyColors[
                        row.difficulty as DifficultyType
                        ] as ColorPaletteProp
                      }
                    >
                      {row.difficulty ?? "None"}
                    </Chip>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Switch
                      color={row.live ? "success" : "danger"}
                      checked={row.live}
                      onChange={() => {
                        requestLive(row.id);
                      }}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Tooltip
                      title="Edit Challenge"
                      color="success"
                      placement="bottom"
                      variant="solid"
                    >
                      <Button
                        color="success"
                        size="sm"
                        variant="plain"
                        aria-label="Edit"
                        component={Link}
                        to={`/managechallenge/${row.id}`}
                      >
                        <EditIcon />
                      </Button>
                    </Tooltip>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <Tooltip
                      title="Delete"
                      color="danger"
                      placement="bottom"
                      variant="solid"
                    >
                      <Button
                        size="sm"
                        color="danger"
                        variant="plain"
                        aria-label="Delete"
                        onClick={() => {
                          requestDelete(row.id);
                        }}
                      >
                        <DeleteForeverIcon />
                      </Button>
                    </Tooltip>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </Sheet>

      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === 0}
          onClick={() => handlePageChange(currentPage - 1)}
          startDecorator={<KeyboardArrowLeftIcon />}
        >
          Previous
        </Button>

        {Array.from({ length: totalPages }).map((_, index) => (
          <IconButton
            key={index}
            size="sm"
            variant={currentPage === index ? "outlined" : "plain"}
            color="neutral"
            onClick={() => handlePageChange(index)}
          >
            {index + 1}
          </IconButton>
        ))}

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          disabled={currentPage === totalPages - 1}
          onClick={() => handlePageChange(currentPage + 1)}
          endDecorator={<KeyboardArrowRightIcon />}
        >
          Next
        </Button>
      </Box>

      <div>
        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog variant="outlined" role="alertdialog">
            <DialogTitle>
              <WarningRoundedIcon />
              Confirmation
            </DialogTitle>
            <Divider />
            <DialogContent>
              Are you sure you want to delete this Challenge?
            </DialogContent>
            <DialogActions>
              <Button variant="solid" color="danger" onClick={handleDelete}>
                Delete
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
      </div>
    </>
  );
};

export default PaginationTable;
